import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notificationAPI, type NotificationResponse } from '../utils/api';
import { buildNotificationImageUrl } from '../utils/notificationImageUrl';
import { useAuth } from '../hooks/useAuth';
import { formatDateTimeInNurseryTimezone } from '../utils/timezone';

// Updated notification data interface matching API response
export interface NotificationData {
  id: number;
  childName: string;
  childId: string;
  classroom: string;
  camera: string;
  imageUrl: string;
  policyName: string;
  alertLevel: string | null;
  isRead: boolean;
  similarity?: number;
  detectionTime: string;
  title: string;
  message: string;
  actionType: string;
  lastSeenAt: string;
  minutesAbsent: number;
  createdAt: string;
  coordinates: { x1: number; y1: number; x2: number; y2: number } | null;
}

// Helper function to convert API response to component format
const convertAPINotificationToComponentFormat = (notification: NotificationResponse, nurseryId: string, timezone: string): NotificationData => {
  const imageUrl = buildNotificationImageUrl(nurseryId, notification.image_id, notification.camera_number);
  
  return {
    id: notification.id,
    childName: notification.child_name,
    childId: notification.child_id,
    classroom: notification.classroom_name,
    camera: notification.camera_name,
    imageUrl: imageUrl,
    policyName: notification.policy_name,
    alertLevel: notification.alert_level,
    isRead: notification.read_flag === 1,
    similarity: notification.similarity ? notification.similarity * 100 : undefined,
    detectionTime: formatDateTimeInNurseryTimezone(notification.log_time, timezone || 'UTC'),
    title: notification.title,
    message: notification.message,
    actionType: notification.action_type,
    lastSeenAt: notification.last_seen_at,
    minutesAbsent: notification.minutes_absent,
    createdAt: notification.created_at,
    coordinates: notification.coordinates,
  };
};

interface NotificationBellProps {
  onNotificationClick: (notification: NotificationData) => void;
}

const BELL_CACHE_DURATION = 30000;
const bellCache: Record<string, { notifications: NotificationData[]; unreadCount: number; timestamp: number }> = {};

const getBellCacheKey = (nurseryId: string, userId: number) => `${nurseryId}_${userId}`;

const NotificationBell: React.FC<NotificationBellProps> = ({ onNotificationClick }) => {
  const { t } = useTranslation('components', { keyPrefix: 'notificationBell' });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { nursery_id } = useParams<{ nursery_id: string }>();
  const { user, currentNursery } = useAuth();

  const nurseryId = nursery_id || currentNursery?.nursery_id;
  
  const CACHE_DURATION = 30000;
  
  const lastFetchTimeRef = useRef<number>(0);
  const hasFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!nurseryId) return;
    setNotifications([]);
    setUnreadCount(0);
    lastFetchTimeRef.current = 0;
    hasFetchedRef.current = false;
  }, [nurseryId]);

  useEffect(() => {
    if (!user?.id || !nurseryId) return;
    const key = getBellCacheKey(nurseryId, user.id);
    const cached = bellCache[key];
    if (cached && Date.now() - cached.timestamp < BELL_CACHE_DURATION) {
      setNotifications(cached.notifications);
      setUnreadCount(cached.unreadCount);
      lastFetchTimeRef.current = cached.timestamp;
      hasFetchedRef.current = true;
    }
  }, [nurseryId, user?.id]);

  const fetchNotifications = useCallback(async (forceRefresh = false) => {
    if (!user?.id || !nurseryId) return;
    
    const now = Date.now();
    const isCacheFresh = (now - lastFetchTimeRef.current) < CACHE_DURATION;
    
    if (!forceRefresh && isCacheFresh && hasFetchedRef.current) {
      console.log('📦 Using cached notifications (fresh data)');
      return;
    }
    
    setLoading(true);
    try {
      const response = await notificationAPI.getNotifications(nurseryId, user.id, { unread_only: true });
      if (response.success) {
        const convertedNotifications = response.data.notifications.map(n => 
          convertAPINotificationToComponentFormat(n, nurseryId, currentNursery?.timezone || 'UTC')
        );
        const ts = Date.now();
        setNotifications(convertedNotifications);
        setUnreadCount(response.data.unread_count);
        lastFetchTimeRef.current = ts;
        hasFetchedRef.current = true;
        if (user?.id) {
          bellCache[getBellCacheKey(nurseryId, user.id)] = {
            notifications: convertedNotifications,
            unreadCount: response.data.unread_count,
            timestamp: ts,
          };
        }
        console.log(`✅ Notifications fetched from API (${convertedNotifications.length} unread)`);
      }
    } catch (error: any) {
      console.error('Failed to fetch notifications:', error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.warn('⚠️ Notification fetch timed out - backend may be slow. Try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id, nurseryId, CACHE_DURATION]);

  useEffect(() => {
    if (!user?.id || !nurseryId) return;
    
    fetchNotifications();
    
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, nurseryId]);

  useEffect(() => {
    if (Boolean(anchorEl)) {
      fetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl]);

  const handleMouseEnter = () => {
    if (!anchorEl) {
      fetchNotifications();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: NotificationData) => {
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
    );
    
    if (!notification.isRead) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    
    try {
      await notificationAPI.markAsRead(notification.id);
      lastFetchTimeRef.current = 0;
      hasFetchedRef.current = false;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, isRead: notification.isRead } : n)
      );
      if (!notification.isRead) {
        setUnreadCount(prev => prev + 1);
      }
    }
    
    handleClose();
    onNotificationClick(notification);
  };

  const getAlertColor = (level: string | null) => {
    switch (level) {
      case 'red':
        return 'error.main';
      case 'yellow':
        return 'warning.main';
      case 'green':
        return 'success.main';
      default:
        return 'grey.500';
    }
  };

  const formatTimeAgo = (notificationItem: NotificationData): string => {
    const createdAt = new Date(notificationItem.detectionTime).getTime();

    if (isNaN(createdAt)) {
      return '';
    }

    const diffInSeconds = Math.floor((Date.now() - createdAt) / 1000);

    if (diffInSeconds < 60) {
      return t('justNow');
    }

   return notificationItem.detectionTime;
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        sx={{
          color: 'white',
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

        <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 420,
            maxHeight: 500,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('title')}
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('unreadCount', { count: unreadCount })}
            </Typography>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('noNotifications')}</Typography>
          </Box>
        ) : (
          notifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={(theme) => ({
                py: 2,
                px: 2,
                backgroundColor: notification.isRead ? 'transparent' : alpha(theme.palette.primary.main, 0.08),
                '&:hover': {
                  backgroundColor: notification.isRead ? theme.palette.action.hover : alpha(theme.palette.primary.main, 0.12),
                },
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'block',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
              })}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: getAlertColor(notification.alertLevel),
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, wordBreak: 'break-word' }}>
                    {t('policyAlert')} {notification.policyName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                    <strong>{notification.childName}</strong> ({notification.childId}) {t('detectedAt')}{' '}
                    <strong>{notification.camera}</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimeAgo(notification)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </MenuItem>
          ))
        )}

        {notifications.length > 0 && [
          <Divider key="divider" />,
          <MenuItem
            key="view-all"
            onClick={() => {
              handleClose();
              if (nursery_id) {
                navigate(`/nursery/${nursery_id}/notifications`);
              } else {
                navigate('/notifications');
              }
            }}
            sx={(theme) => ({
              justifyContent: 'center',
              color: 'primary.main',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            })}
          >
            {t('viewAll')}
          </MenuItem>
        ]}
      </Menu>
    </>
  );
};

export default NotificationBell;
